/**
 * API problems in the reader's language (docs/contributing/i18n.md, "API problems"). Every problem the API sends has a
 * stable `code` (`plan_limit`, `insufficient_lux`…) and maybe `params`; its `detail` is English. The dashboard shows:
 * - in English, the server's own sentence (`detail`: the most precise wording);
 * - in another language, the catalog's text of that code (`errors.code.<camelCase>`, namespace `errors`) when it has
 *   one, else the server's sentence.
 * `errorMessage()` (api/client.ts) goes through here, so every place that shows an API error is covered without
 * knowing about languages. The provider registers the current language's catalog (`registerProblemTranslator`).
 */

/** `plan_limit` → `errors.code.planLimit`. */
export function problemKey(code: string): string {
  return `errors.code.${code.replace(/[-_]+([a-z0-9])/gi, (_, c: string) => c.toUpperCase())}`;
}

type Translator = { locale: string; has: (key: string) => boolean; format: (key: string, values?: Record<string, unknown>) => string };
let current: Translator | null = null;

export function registerProblemTranslator(translator: Translator | null): void {
  current = translator;
}

/** The reader's text of a problem: null when the server's own sentence should stand (English, or a code the catalog
 * does not know). */
export function translatedProblem(problem: { code?: string | null; params?: Record<string, unknown> | null } | undefined): string | null {
  if (!current || !problem?.code || current.locale === "en") return null;
  const key = problemKey(problem.code);
  if (!current.has(key)) return null;
  try {
    return current.format(key, problem.params ?? undefined);
  } catch {
    return null;
  }
}

/** A catalog text outside React (the problem translator's language), or null before the provider has registered. */
export function translateOutside(key: string, values?: Record<string, unknown>): string | null {
  if (!current || !current.has(key)) return null;
  try {
    return current.format(key, values);
  } catch {
    return null;
  }
}
